
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { SubscriptionStatus } from "@/services/SubscriptionService";
import { EmailService } from "@/services/EmailService";

interface CheckoutHandlerProps {
  user: any;
  subscriptionStatus: SubscriptionStatus | null;
  forceRefresh?: () => void;
  subscriptionChecked: boolean;
  setSubscriptionChecked: (checked: boolean) => void;
}

export const CheckoutHandler = ({
  user,
  subscriptionStatus,
  forceRefresh,
  subscriptionChecked,
  setSubscriptionChecked
}: CheckoutHandlerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const processingRef = useRef(false);
  const initialCheckPerformedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const emailSentRef = useRef(false);

  useEffect(() => {
    // Only run checkout handling logic once per page load
    if (processingRef.current || initialCheckPerformedRef.current) return;
    
    // Check for possible scenarios where we need to refresh the subscription status
    const urlParams = new URLSearchParams(location.search);
    const checkoutSuccess = urlParams.get('success') === 'true';
    const justActivated = localStorage.getItem('subscription_activated');
    
    if ((justActivated || checkoutSuccess) && user && forceRefresh && !subscriptionChecked) {
      processingRef.current = true;
      initialCheckPerformedRef.current = true;
      
      console.log('Detected checkout or activation, refreshing subscription status');
      
      // Immediately mark the subscription as active in localStorage
      // This ensures we don't block the user even if API calls are delayed
      localStorage.setItem('subscription_confirmed_active', 'true');
      
      // Notify components about the subscription update
      window.dispatchEvent(new Event('subscription_updated'));
      
      // Still refresh to make sure backend is updated
      forceRefresh();
      setSubscriptionChecked(true);
      setLastRefreshTime(Date.now());
      
      // Clear success params from URL while keeping the current route
      if (checkoutSuccess) {
        navigate(location.pathname, { replace: true });
        
        // Send confirmation email if checkout was successful
        // Only after the checkout is successful, not when just the URL is created
        if (!emailSentRef.current && user.email) {
          const plan = localStorage.getItem('selected_plan') || 'Premium';
          console.log('Sending subscription confirmation email after successful checkout');
          
          // Check if this is a test mode subscription
          const isTestMode = plan.toLowerCase().includes('test') || 
                            localStorage.getItem('stripe_test_mode') === 'true';
          
          // Generate the actual plan name with test indicator if needed
          const planName = `${plan}${isTestMode ? ' (Test)' : ''}`;
          
          EmailService.sendSubscriptionEmail(
            user.email, 
            planName, 
            user.user_metadata?.first_name || ''
          ).then(() => {
            emailSentRef.current = true;
            console.log('Subscription confirmation email sent successfully');
          }).catch(error => {
            console.error('Failed to send subscription email:', error);
          });
          
          // Clear the selected plan from localStorage
          localStorage.removeItem('selected_plan');
        }
      }
      
      // Show toast for subscription active
      if (checkoutSuccess || justActivated) {
        toast({
          title: "Subscription Active!",
          description: "Your subscription is active with unlimited transformations!",
          duration: 5000,
        });
        
        // Clear any activation flags once we've confirmed subscription is active
        localStorage.removeItem('subscription_activated');
        localStorage.removeItem('subscription_activated_time');
        localStorage.removeItem('stripe_test_mode');
      }
      
      // Set flag to force multiple refresh attempts with increasing delay
      // to ensure the database state is eventually consistent
      if (refreshAttempts < 3) {
        const delay = (refreshAttempts + 1) * 3000; // Increasing delay: 3s, 6s, 9s
        timeoutRef.current = window.setTimeout(() => {
          console.log(`Performing delayed subscription refresh attempt ${refreshAttempts + 1} after ${delay}ms`);
          if (forceRefresh) forceRefresh();
          setRefreshAttempts(prev => prev + 1);
          setLastRefreshTime(Date.now());
          processingRef.current = false;
        }, delay);
      } else {
        processingRef.current = false;
      }
    }
  }, [location, user, forceRefresh, subscriptionChecked, toast, setSubscriptionChecked, navigate, refreshAttempts]);

  // Update subscription checked flag when subscription status changes
  useEffect(() => {
    if (subscriptionStatus && subscriptionStatus.isActive) {
      console.log('Subscription is now active in database, updating localStorage');
      setSubscriptionChecked(true);
      
      // Set a flag in localStorage to indicate the subscription is confirmed active
      localStorage.setItem('subscription_confirmed_active', 'true');
      
      // Dispatch an event to notify other components
      window.dispatchEvent(new Event('subscription_updated'));
      
      // Clear any activation flags once we've confirmed subscription is active
      localStorage.removeItem('subscription_activated');
      localStorage.removeItem('subscription_activated_time');
      localStorage.removeItem('stripe_test_mode');
      processingRef.current = false;
    }
  }, [subscriptionStatus, setSubscriptionChecked]);
  
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return null; // This is just a logic component, no UI
};
